import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Users, FileText, CalendarClock, ClipboardCheck, UserSquare2,
  BarChart3, Wallet, UsersRound, LogOut, Moon, Sun, Landmark, Menu, X, Settings, Wrench,
  Building2, UploadCloud, History, Inbox, FolderOpen,
} from "lucide-react";

const ALL = ["Admin", "Manager", "Team Leader", "Telecaller", "Coordinator"];
const MGMT = ["Admin", "Manager", "Team Leader"];
const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true, roles: "*" },
  { to: "/queries", label: "Queries", icon: Inbox, testid: "nav-queries", roles: "*" },
  { to: "/coord-files", label: "Coord Files", icon: FolderOpen, testid: "nav-coord-files", roles: ["Admin", "Manager", "Team Leader", "Coordinator"] },
  { to: "/leads", label: "Leads", icon: Users, testid: "nav-leads", roles: ["Admin", "Manager", "Team Leader", "Telecaller"] },
  { to: "/import-leads", label: "Import Leads", icon: UploadCloud, testid: "nav-import-leads", roles: MGMT },
  { to: "/import-history", label: "Import History", icon: History, testid: "nav-import-history", roles: MGMT },
  { to: "/followups", label: "Follow-ups", icon: CalendarClock, testid: "nav-followups", roles: "*" },
  { to: "/applications", label: "Applications", icon: FileText, testid: "nav-applications", roles: ["Admin", "Manager", "Team Leader", "Coordinator"] },
  { to: "/clients", label: "Clients", icon: UserSquare2, testid: "nav-clients", roles: ["Admin", "Manager", "Team Leader", "Coordinator", "Telecaller"] },
  { to: "/attendance", label: "Attendance", icon: ClipboardCheck, testid: "nav-attendance", roles: "*" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics", roles: MGMT },
  { to: "/tools", label: "Tools", icon: Wrench, testid: "nav-tools", roles: "*" },
  { to: "/companies", label: "Companies", icon: Building2, testid: "nav-companies", roles: MGMT },
  { to: "/payslips", label: "Payslips", icon: Wallet, testid: "nav-payslips", roles: "*" },
  { to: "/team", label: "Team", icon: UsersRound, testid: "nav-team", roles: MGMT },
  { to: "/settings", label: "Admin Panel", icon: Settings, testid: "nav-settings", roles: ["Admin"] },
];

export function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => localStorage.getItem("ls_theme") === "dark");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("ls_theme", dark ? "dark" : "light");
  }, [dark]);

  const role = user?.role;
  const { data: qCount } = useQuery({
    queryKey: ["queries-count-nav"],
    queryFn: async () => (await api.get("/queries/count")).data,
    enabled: !!user,
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
  });
  const openQueries = qCount?.inbox_open ?? qCount?.total_open ?? 0;
  const items = NAV.filter((n) => n.roles === "*" || (Array.isArray(n.roles) && n.roles.includes(role)));

  const handleLogout = () => { logout(); navigate("/login"); };

  const SidebarInner = (
    <>
      <div className="flex items-center gap-2.5 px-2 py-3 mb-3">
        <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center">
          <Landmark className="h-5 w-5 text-white" />
        </div>
        <div>
          <div className="font-heading font-bold text-white text-sm leading-tight">LoanSupport<span className="text-blue-400">India</span></div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">DSA CRM · Web</div>
        </div>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto">
        {items.map(({ to, label, icon: Icon, testid, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={testid}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive ? "bg-blue-600 text-white shadow-sm" : "text-slate-400 hover:bg-slate-800 hover:text-white"
              }`
            }
          >
            <Icon style={{ width: 18, height: 18 }} />
            <span className="flex-1">{label}</span>
            {to === "/queries" && openQueries > 0 && (
              <span data-testid="queries-nav-badge" className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-semibold">{openQueries > 99 ? "99+" : openQueries}</span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-slate-800 pt-3 mt-2">
        <div className="px-3 py-2 mb-1">
          <div className="text-sm font-medium text-white truncate">{user?.name || "User"}</div>
          <div className="text-xs text-slate-500 truncate">{user?.role} · {user?.employee_id || ""}</div>
        </div>
        <button onClick={handleLogout} data-testid="logout-button" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:bg-rose-950/50 hover:text-rose-300 transition-colors">
          <LogOut style={{ width: 18, height: 18 }} /> Logout
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <aside className="fixed left-0 top-0 h-screen w-64 bg-slate-900 border-r border-slate-800 z-40 hidden md:flex flex-col p-4">{SidebarInner}</aside>

      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <aside className="relative h-full w-64 bg-slate-900 flex flex-col p-4">{SidebarInner}</aside>
        </div>
      )}

      <div className="md:pl-64 min-h-screen flex flex-col">
        <header className="sticky top-0 z-30 h-16 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 px-4 sm:px-6 flex items-center justify-between">
          <button className="md:hidden text-slate-600 dark:text-slate-300" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button">
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-1">
            <span className="hidden sm:inline text-sm text-slate-500 mr-2">{user?.name} · <span className="font-medium text-slate-700 dark:text-slate-300">{user?.role}</span></span>
            <Button variant="ghost" size="icon" onClick={() => setDark((d) => !d)} data-testid="theme-toggle" className="text-slate-600 dark:text-slate-300">
              {dark ? <Sun style={{ width: 18, height: 18 }} /> : <Moon style={{ width: 18, height: 18 }} />}
            </Button>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto animate-fade-up">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
